/**
 * What the composition editor's header row offers, entity by entity. The table is the whole
 * declaration: a new entity is a new row the compiler demands, and an entity that goes away is
 * the row deleted.
 */
import type { useCompositionHeaderAction } from "../builder";
import type { InstrumentEntities } from "../const";
import type { MarketDateControl } from "../quantity/market-date/useMarketDate";

/** The actions {@link useCompositionHeaderAction} can build. A name the hook cannot build is a type error in the table below. */
export type AddMenuActionKey = keyof ReturnType<typeof useCompositionHeaderAction>;

/** The Add menu of one entity, in the order the user sees it. An empty list renders the trigger disabled. */
export type AddMenu = readonly AddMenuActionKey[];

type QuickActionsConfig = {
	/** The decimals stepper, which rounds the weight columns. */
	decimals: boolean;
	/** The group-by-classification select. */
	groupByClassification: boolean;
	addMenu: Record<"new" | "edit", AddMenu>;
};

// The menus are spread together from these blocks, in the order the user sees them: the pickers
// that add rows, then the two actions that bring in a whole composition.
const ADD_INSTRUMENTS: AddMenu = ["addInstruments"];
const ADD_PORTFOLIO: AddMenu = [...ADD_INSTRUMENTS, "addPortfolio"];
const TAKE_WHOLE_COMPOSITION: AddMenu = ["uploadInstruments", "copyComposition"];

/**
 * What a quantity composition keeps of an entity's menu: the pickers that add rows, and the upload,
 * which takes the file's rows by their quantities (`../quantity/upload`).
 */
const QUANTITY_ACTIONS: ReadonlySet<AddMenuActionKey> = new Set([...ADD_PORTFOLIO, "uploadInstruments"]);

const ADD_PORTFOLIO_UPLOAD: AddMenu = [...ADD_PORTFOLIO, "uploadInstruments"];
const ADD_PORTFOLIO_UPLOAD_COPY: AddMenu = [...ADD_PORTFOLIO, ...TAKE_WHOLE_COMPOSITION];
const ADD_UPLOAD_COPY: AddMenu = [...ADD_INSTRUMENTS, ...TAKE_WHOLE_COMPOSITION];
const NOTHING: AddMenu = [];

/** Most entities offer the same menu whether the composition is new or being edited. */
function inEitherMode(add: AddMenu): QuickActionsConfig["addMenu"] {
	return { new: add, edit: add };
}

const QUICK_ACTIONS_BY_ENTITY: Record<InstrumentEntities, QuickActionsConfig> = {
	INVESTMENT: {
		decimals: true,
		groupByClassification: true,
		addMenu: inEitherMode(ADD_PORTFOLIO_UPLOAD_COPY),
	},
	INVESTMENT_DRAFT: {
		decimals: true,
		groupByClassification: true,
		addMenu: inEitherMode(ADD_PORTFOLIO_UPLOAD_COPY),
	},
	// A universe's rows carry no weight to round and no classification to group by, and there is
	// no weighting to copy off another composition.
	UNIVERSE: {
		decimals: false,
		groupByClassification: false,
		addMenu: inEitherMode(ADD_PORTFOLIO_UPLOAD),
	},
	// A reference composition cannot absorb a whole portfolio.
	TARGET_INVESTMENT: {
		decimals: true,
		groupByClassification: true,
		addMenu: inEitherMode(ADD_UPLOAD_COPY),
	},
	BENCHMARK: {
		decimals: true,
		groupByClassification: true,
		addMenu: inEitherMode(ADD_UPLOAD_COPY),
	},
	// An enhancement takes its composition from the proposal it enhances, so a brand-new one has
	// nothing to add to.
	INVESTMENT_ENHANCEMENT: {
		decimals: true,
		groupByClassification: true,
		addMenu: { new: NOTHING, edit: ADD_PORTFOLIO },
	},
};

export type ResolvedQuickActions = {
	decimals: boolean;
	groupByClassification: boolean;
	/** An empty list renders the trigger disabled. */
	addMenu: AddMenu;
	/** The market date control, or `null` where the surface has no day to offer. */
	marketDate: MarketDateControl | null;
};

/**
 * What the header row is resolved against: the editor's own props, narrowed to the fields that
 * decide a control. Whether there is a date to offer is settled by the caller, which is the one
 * place that knows — so it arrives as a value, not as a builder to interrogate.
 */
export type QuickActionsContext = {
	entity: InstrumentEntities;
	mode: "new" | "edit";
	compositionMode?: "weight" | "quantity";
	marketDate?: MarketDateControl | null;
};

export function resolveQuickActions(ctx: QuickActionsContext): ResolvedQuickActions {
	const config = QUICK_ACTIONS_BY_ENTITY[ctx.entity];
	return {
		decimals: config.decimals,
		groupByClassification: config.groupByClassification,
		// Quantity mode (MDOT-25245) offers the entity's own menu narrowed to QUANTITY_ACTIONS:
		// `copyComposition` REPLACES the whole composition with weight-only rows, which would destroy
		// every quantity and price in it. Narrowing the entity's list rather than declaring a second
		// one keeps the new/edit distinction — a composition with nothing to add to still resolves to
		// an empty list here too.
		addMenu:
			ctx.compositionMode === "quantity"
				? config.addMenu[ctx.mode].filter((action) => QUANTITY_ACTIONS.has(action))
				: config.addMenu[ctx.mode],
		// The day the composition is edited against (MDOT-25190), in both modes (MDOT-25192). The
		// caller hands it over or says nothing; nothing means no control.
		marketDate: ctx.marketDate ?? null,
	};
}
